<template>
    <card :loading="loading">
        <div class="flex flex-col gap-3 dark:text-white">
            <template v-if="transfers.length > 0">
                <div
                    v-for="t in transfers"
                    :key="t.id"
                    class="flex items-center"
                >
                    <div
                        class="-left-1.5 mr-2 h-3 w-3 rounded-full border border-white dark:border-gray-900"
                        :class="dotClass(t)"
                    ></div>
                    <span class="text-sm">
                        <span class="mr-1">{{ t.created_at }}</span>
                        Pix {{ statusLabel(t) }} em
                        <span class="font-medium uppercase">{{
                            t.provider
                        }}</span>
                        <span v-if="t.slice_type && t.slice_type !== 'full'">
                            ({{ sliceLabel(t.slice_type) }})
                        </span>
                        <span v-if="t.amount" class="ml-1 text-gray-500">
                            — {{ formatAmount(t.amount) }}
                        </span>
                    </span>
                </div>
            </template>

            <template v-else>
                <div class="flex flex-col gap-2 text-sm">
                    <div class="font-medium">Transferência não encontrada</div>
                    <div
                        v-if="planPreview"
                        class="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    >
                        {{ planPreview }}
                    </div>

                    <select
                        v-model="selectedAccountId"
                        class="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-black focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    >
                        <option
                            v-for="account in accounts"
                            :key="account.id"
                            :value="account.id"
                        >
                            {{ account.name }} - Ag: {{ account.payer_agency }}
                            Cc: {{ account.payer_account }}
                        </option>
                    </select>

                    <label
                        v-if="canForceItau"
                        class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200"
                    >
                        <input
                            type="checkbox"
                            v-model="forceFullItau"
                            class="rounded border-gray-300"
                        />
                        Enviar tudo pelo Itaú
                    </label>

                    <button
                        @click.prevent="submit"
                        :disabled="!selectedAccountId || loading"
                        class="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <spinner class="mr-2 inline" v-if="loading" />
                        Enviar Transferência
                    </button>
                </div>
            </template>
        </div>
    </card>
</template>

<script>
import Card from "../layouts/Card.vue";
import Spinner from "../forms/Spinner.vue";

export default {
    components: { Spinner, Card },
    props: ["cashout"],

    data() {
        return {
            loading: false,
            transfers: [],
            accounts: [],
            selectedAccountId: null,
            plan: null,
            forceFullItau: false,
        };
    },

    computed: {
        canForceItau() {
            if (!this.plan || !this.plan.feature_enabled) {
                return false;
            }
            if (!this.plan.eligible) {
                return false;
            }
            const p = this.plan.plan;
            return p && (p.is_full_cc || p.is_mixed);
        },
        planPreview() {
            if (!this.plan) {
                return "";
            }
            const p = this.plan.plan;
            const fallback = this.formatAmount(this.plan.fallback_amount);

            if (!this.plan.feature_enabled || !this.plan.eligible) {
                return `Pagamento via Itaú (${fallback})`;
            }

            if (!p || p.is_empty || p.is_full_non_cc) {
                return `Pagamento via Itaú (${fallback})`;
            }

            if (p.is_full_cc) {
                return `Pagamento 100% via BDR (${this.formatAmount(p.bdr.amount)})`;
            }

            if (p.is_mixed) {
                return (
                    `BDR ${this.formatAmount(p.bdr.amount)} (CC) + ` +
                    `Itaú ${this.formatAmount(p.itau.amount)} (demais)`
                );
            }

            return `Pagamento via Itaú (${fallback})`;
        },
    },

    mounted() {
        this.fetch();
        this.fetchAccounts();
        this.fetchPlan();
    },

    methods: {
        formatAmount(value) {
            if (value === null || value === undefined) {
                return "";
            }
            if (this.$root && typeof this.$root.formatCurrency === "function") {
                return this.$root.formatCurrency(value);
            }
            return value;
        },
        sliceLabel(slice) {
            switch (slice) {
                case "cc":
                    return "cartão";
                case "non_cc":
                    return "demais";
                case "full":
                    return "total";
                default:
                    return slice;
            }
        },
        dotClass(t) {
            return {
                "bg-green-500": ["authorized", "completed"].includes(t.status),
                "bg-red-500": ["rejected", "rejected_on_file"].includes(
                    t.status,
                ),
                "bg-yellow-500": [
                    "pending_authorization",
                    "pending_execution",
                    "pending_change",
                ].includes(t.status),
            };
        },
        statusLabel(t) {
            switch (t.status) {
                case "pending_authorization":
                    return "Autorização Pendente";
                case "pending_execution":
                    return "Execução Pendente";
                case "authorized":
                    return "Autorizado";
                case "completed":
                    return "Transferido";
                case "rejected":
                    return "Rejeitado";
                case "pending_change":
                    return "Mudança Cancelada";
                case "rejected_on_file":
                    return "Rejeitado";
                default:
                    return t.status;
            }
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/cashout/" + this.cashout.id + "/transfer")
                .then(({ data }) => {
                    this.transfers = Array.isArray(data) ? data : [];
                    this.loading = false;
                })
                .catch(() => {
                    this.transfers = [];
                    this.loading = false;
                });
        },
        fetchAccounts() {
            axios
                .get("/api/financial/wire-transfer-accounts")
                .then(({ data }) => {
                    this.accounts = data.data;
                    const defaultAccount = this.accounts.find(
                        (account) => account.is_default,
                    );
                    this.selectedAccountId = defaultAccount
                        ? defaultAccount.id
                        : this.accounts[0]?.id;
                })
                .catch((error) => {
                    console.error(
                        "Failed to fetch wire transfer accounts:",
                        error,
                    );
                });
        },
        fetchPlan() {
            axios
                .get("/api/cashout/" + this.cashout.id + "/transfer/plan")
                .then(({ data }) => {
                    this.plan = data;
                })
                .catch(() => {
                    this.plan = null;
                });
        },
        submit() {
            if (!this.selectedAccountId) {
                return;
            }

            this.loading = true;
            axios
                .post("/api/cashout/" + this.cashout.id + "/transfer", {
                    wire_transfer_account_id: this.selectedAccountId,
                    force_full_itau: this.forceFullItau,
                })
                .then(() => {
                    this.fetch();
                })
                .catch(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
